import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    IconTick,
    IconClose
} from 'svgIcons/MotionPortalIcons';
import './_style.scss';

const Stepper = ({
    steps,
    activeStep = 0,
    orientation = 'horizontal',
    onChange,
    className = '',
    stepClassName = '',
    connectorClassName = '',
    completedStepClassName = '',
    activeStepClassName = '',
    errorStepClassName = '',
    labelClassName = '',
    disabled = false,
    errorSteps = [],
    disabledSteps = []
}) => {
    const [currentStep, setCurrentStep] = useState(activeStep);

    useEffect(() => {
        setCurrentStep(activeStep);
    }, [activeStep]);

    const handleStepClick = (index) => {
        if (disabled || disabledSteps.includes(index)) { return; }

        setCurrentStep(index);
        if (onChange) {
            onChange(index);
        }
    };

    return (
        <div
            className={`stepper stepper-${orientation} ${className}`}
            data-testid='stepper'
        >
            {steps.map((step, index) => {
                const isCompleted = index < currentStep;
                const isActive = index === currentStep;
                const isError = errorSteps.includes(index);
                const isDisabled = disabledSteps.includes(index);
                const isLast = index === steps.length - 1;

                return (
                    <React.Fragment key={index}>
                        <div
                            className={`stepper-item ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${isError ? 'error' : ''} ${isDisabled ? 'disabled' : ''} ${stepClassName}`}
                            data-testid={`stepper-item-${index}`}
                        >
                            <div className='step-content'>
                                <button
                                    type='button'
                                    className={`step-counter ${isCompleted ? completedStepClassName : ''} ${isActive ? activeStepClassName : ''} ${isError ? errorStepClassName : ''}`}
                                    onClick={() => handleStepClick(index)}
                                    disabled={disabled || disabledSteps.includes(index)}
                                    data-testid={`step-counter-${index}`}
                                >
                                    {isError ?
                                        <span className='step-error' aria-hidden='true'>
                                            <IconClose color='#ffffff' width={16} height={16} />
                                        </span>
                                        : isCompleted ?
                                            <span className='step-check' aria-hidden='true'>
                                                <IconTick color='#ffffff' />
                                            </span>
                                            :
                                            index + 1
                                    }
                                </button>

                                <div className='step-text'>
                                    <div className={`step-label ${labelClassName}`} data-testid={`step-label-${index}`}>
                                        {step.label}
                                    </div>

                                    {step.description &&
                                        <div className='step-description' data-testid={`step-description-${index}`}>
                                            {step.description}
                                        </div>
                                    }
                                </div>
                            </div>

                            {!isLast && orientation === 'vertical' &&
                                <div
                                    className={`step-connector ${isCompleted ? 'completed' : ''} ${isError ? 'error' : ''} ${connectorClassName}`}
                                    data-testid={`step-connector-${index}`}
                                />
                            }
                        </div>

                        {!isLast && orientation === 'horizontal' &&
                            <div
                                className={`step-connector-horizontal ${isCompleted ? 'completed' : ''} ${isError ? 'error' : ''} ${connectorClassName}`}
                                data-testid={`step-connector-horizontal-${index}`}
                            />
                        }
                    </React.Fragment>
                );
            })}
        </div>
    );
};

Stepper.propTypes = {
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.node.isRequired,
            description: PropTypes.node
        })
    ).isRequired,
    activeStep: PropTypes.number,
    orientation: PropTypes.oneOf(['horizontal', 'vertical']),
    onChange: PropTypes.func,
    className: PropTypes.string,
    stepClassName: PropTypes.string,
    connectorClassName: PropTypes.string,
    completedStepClassName: PropTypes.string,
    activeStepClassName: PropTypes.string,
    errorStepClassName: PropTypes.string,
    labelClassName: PropTypes.string,
    disabled: PropTypes.bool,
    errorSteps: PropTypes.arrayOf(PropTypes.number),
    disabledSteps: PropTypes.arrayOf(PropTypes.number)
};

export default Stepper;
